import { ArrowRight, ChevronRight, CreditCard, Wallet, X, type LucideIcon } from "lucide-react";
import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { CARD_WALLET_SETTINGS_PATH } from "@/modules/dashboard/settings/constants/cardWalletSection";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

const DISMISS_KEY_PREFIX = "coloca:card-wallet-invite-dismissed:";

interface Step {
  icon: LucideIcon;
  label: "card_wallet_invite_step_withdraw" | "card_wallet_invite_step_hold" | "card_wallet_invite_step_load";
  isWallet?: boolean;
}

const STEPS: Step[] = [
  { icon: CreditCard, label: "card_wallet_invite_step_withdraw" },
  { icon: Wallet, label: "card_wallet_invite_step_hold", isWallet: true },
  { icon: CreditCard, label: "card_wallet_invite_step_load" },
];

// `WalletBalanceBanner`'s dots, drawn in the brand green and faded out towards the copy so they never sit
// under the text.
const DOT_TEXTURE: CSSProperties = {
  backgroundImage:
    "radial-gradient(color-mix(in oklab, var(--primary) 22%, transparent) 1px, transparent 1px)",
  backgroundSize: "8px 8px",
  maskImage: "linear-gradient(to left, black, transparent 70%)",
  WebkitMaskImage: "linear-gradient(to left, black, transparent 70%)",
};

function readDismissed(organizationId: string | null): boolean {
  if (!organizationId) return false;
  try {
    return window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${organizationId}`) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(organizationId: string | null): void {
  if (!organizationId) return;
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${organizationId}`, "1");
  } catch {
    // Storage refused (private window): the card comes back next visit, which is harmless.
  }
}

export interface CardWalletInviteCardProps {
  organizationId: string | null;
  onLearnMore: () => void;
}

/**
 * Where the card wallet banner would be, for an organization without one: a quiet card saying it exists and
 * what it is for, dismissible per organization in this browser. Never a balance — there is none to show.
 * Dismissing it says where the wallet can still be turned on (Settings), so hiding the line never hides the
 * way.
 */
export function CardWalletInviteCard({ organizationId, onLearnMore }: CardWalletInviteCardProps) {
  const { t } = useTranslations("cards");
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => readDismissed(organizationId));

  const dismiss = () => {
    writeDismissed(organizationId);
    setDismissed(true);
    colocaToast.info(t("card_wallet_invite_dismissed_title"), {
      description: t("card_wallet_invite_dismissed_body"),
      action: {
        label: t("card_wallet_invite_dismissed_action"),
        onClick: () => navigate(CARD_WALLET_SETTINGS_PATH),
      },
      duration: 8000,
    });
  };

  if (dismissed) return null;

  return (
    // The quiet sibling of `WalletBalanceBanner`, which takes this place once the wallet is on: same eyebrow
    // and status pill, same dot texture, in a tint of the brand green instead of the solid gradient. Laid out
    // against the card's own width (`@container`), never the viewport's — a `flex-1` text column with a zero
    // basis once squeezed the copy into a word-per-line strip on a phone. Narrow: copy, how it works, button.
    // Wide: copy and button on the left, how it works on the right.
    <section className="@container relative overflow-hidden rounded-2xl border border-primary/25 bg-linear-to-br from-primary/10 to-primary/[0.03] p-5 dark:border-primary/35 dark:from-primary/20 dark:to-primary/5">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={DOT_TEXTURE} />
      <ColocaIconButton
        icon={X}
        size="sm"
        variant="ghost"
        className="absolute top-3 right-3"
        aria-label={t("card_wallet_invite_dismiss")}
        onClick={dismiss}
      />
      <div className="relative grid gap-5 @3xl:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] @3xl:gap-x-10 @3xl:gap-y-4 @3xl:pr-8">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 pr-8 text-muted-foreground">
            <Wallet aria-hidden="true" className="size-4 text-primary" />
            <span className="font-mono text-[11px] uppercase tracking-wider">
              {t("card_wallet_invite_eyebrow")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
              <span aria-hidden="true" className="size-1.5 rounded-full border border-muted-foreground/70" />
              {t("card_wallet_invite_status")}
            </span>
          </div>
          <h2 className="mt-3 text-base font-semibold leading-snug text-foreground">
            {t("card_wallet_invite_title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("card_wallet_invite_body")}</p>
        </div>

        {/* A real sequence, so an ordered list: what the wallet is for, said as the money's path. */}
        <ol
          aria-label={t("card_wallet_invite_steps_label")}
          className="grid grid-cols-3 gap-2 rounded-xl border border-primary/15 bg-card/80 px-2 py-4 @3xl:col-start-2 @3xl:row-span-2 @3xl:row-start-1 @3xl:self-center @3xl:px-4"
        >
          {STEPS.map(({ icon: Icon, label, isWallet }, index) => (
            <li key={label} className="relative flex flex-col items-center text-center">
              {index < STEPS.length - 1 && (
                // From this node's edge to the next one's, across the column gap.
                <span
                  aria-hidden="true"
                  className="absolute top-5 right-[calc(-50%+1rem)] left-[calc(50%+1.5rem)] flex -translate-y-1/2 items-center text-primary/60"
                >
                  <span className="flex-1 border-t border-dashed border-current" />
                  <ChevronRight className="-ml-1.5 size-3.5" />
                </span>
              )}
              <span
                className={cn(
                  "inline-flex size-10 items-center justify-center rounded-xl",
                  isWallet
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-card text-muted-foreground ring-1 ring-border",
                )}
              >
                <Icon aria-hidden="true" className="size-5" />
              </span>
              <span
                className={cn(
                  "mt-2 text-[11px] leading-snug",
                  isWallet ? "font-medium text-foreground" : "text-muted-foreground",
                )}
              >
                {t(label)}
              </span>
            </li>
          ))}
        </ol>

        <div className="@3xl:col-start-1 @3xl:row-start-2">
          <ColocaButton
            size="sm"
            variant="default"
            iconTrailing={ArrowRight}
            className="w-full @md:w-auto"
            onClick={onLearnMore}
          >
            {t("card_wallet_invite_cta")}
          </ColocaButton>
        </div>
      </div>
    </section>
  );
}
